/**
 * Consult Scheduling — PURE availability engine (Stage 1 · foundation).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  `computeOpenSlots(...)` — the deterministic core that turns
 *    (busy intervals)  ∩  (the weekly bookable windows + global rules)
 *  into a list of bookable open slots. It is the portal-side mirror of the
 *  working-calendar resolver in `src/lib/workingCalendar.ts`: same Intl-based
 *  zone arithmetic, same "operate in wall-clock minutes within the configured
 *  zone" discipline, and the SAME purity contract — no Firebase, no React, and
 *  crucially **no `Date.now()` inside** (the caller passes `nowISO`) so the
 *  Node native test runner exercises every rule deterministically.
 *
 *  In production Google Calendar free/busy is the source of truth for "who is
 *  actually free" (plan §1). That data arrives here as the `FreeBusyInterval[]`
 *  SEAM below — a Cloud Function reads free/busy with the service account and
 *  feeds it in. This engine never talks to Google; it only intersects the busy
 *  intervals with the bookable shape (`settings/availability`). That keeps the
 *  credential-bound integration out of the testable core entirely.
 *
 *  Rules honoured (all owner-locked, plan §6):
 *    · slots fall ONLY inside the day's windows
 *    · busy intervals are subtracted, each expanded by `bufferMin` on both
 *      sides → never back-to-back with an existing event
 *    · `minNoticeHours` minimum advance notice from `nowISO`
 *    · `maxPerDay` cap on bookings per calendar day (existing busy bookings on
 *      a day count against the cap)
 *    · `horizonDays` bookable horizon
 *    · hard-blocked days skipped (Sundays via empty windows + a passed-in
 *      blocked-dates list)
 * ─────────────────────────────────────────────────────────────────────────
 */
import type { Audience, Weekday, WeeklyAvailability } from './types';

/**
 * Busy interval SEAM — the shape Google Calendar free/busy is normalised into
 * before it reaches this engine. A Cloud Function (admin SDK + service account)
 * produces these from `events.list` on the one shared calendar; the engine
 * never reads Google itself. Both ends are ISO instant strings.
 */
export interface FreeBusyInterval {
  /** Start of the busy block (ISO instant). */
  startISO: string;
  /** End of the busy block (ISO instant). */
  endISO: string;
}

/** One bookable open slot returned by the engine. */
export interface OpenSlot {
  /** Slot start (ISO instant). */
  startISO: string;
  /** Slot end (ISO instant) = startISO + durationMin. */
  endISO: string;
  /** Audience tag inherited from the window the slot falls in. */
  audienceTag: Audience;
}

/** The booking rules + clock the engine resolves slots against. */
export interface ComputeOpenSlotsParams {
  /** Slot / call length in minutes. */
  durationMin: number;
  /** Gap enforced around every busy interval (never back-to-back). */
  bufferMin: number;
  /** Minimum advance notice in hours, measured from `nowISO`. */
  minNoticeHours: number;
  /** Max bookings per calendar day (busy bookings on the day count against it). */
  maxPerDay: number;
  /** Bookable horizon in days from `nowISO`. */
  horizonDays: number;
  /** The clock — caller-supplied so the engine stays pure / deterministic. */
  nowISO: string;
  /** IANA zone the windows are anchored in (the stored truth, e.g. Asia/Kolkata). */
  timeZone: string;
  /** Hard-blocked full days as `YYYY-MM-DD` in `timeZone`. Sundays are handled
   *  separately via empty weekday windows. */
  blockedDates?: string[];
}

/* ── zone-aware date parts (Intl only — same approach as workingCalendar.ts) ── */

interface Parts {
  y: number;
  mo: number;
  d: number;
  min: number; // minutes past local midnight
  dow: Weekday;
}

function partsInZone(date: Date, tz: string): Parts {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'short',
  });
  const p: Record<string, string> = {};
  for (const part of fmt.formatToParts(date)) p[part.type] = part.value;
  const dowMap: Record<string, Weekday> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  let hour = parseInt(p.hour, 10);
  if (hour === 24) hour = 0; // some ICU builds emit "24" at midnight
  return {
    y: parseInt(p.year, 10),
    mo: parseInt(p.month, 10),
    d: parseInt(p.day, 10),
    min: hour * 60 + parseInt(p.minute, 10),
    dow: dowMap[p.weekday],
  };
}

function ymd(p: Parts): string {
  const mm = String(p.mo).padStart(2, '0');
  const dd = String(p.d).padStart(2, '0');
  return `${p.y}-${mm}-${dd}`;
}

/** Offset (ms) of `tz` from UTC at the instant `utcMs`: wall-clock − UTC. */
function zoneOffsetMsAt(utcMs: number, tz: string): number {
  const p = partsInZone(new Date(utcMs), tz);
  const wallAsUtc = Date.UTC(
    p.y,
    p.mo - 1,
    p.d,
    Math.floor(p.min / 60),
    p.min % 60,
    0,
  );
  return wallAsUtc - utcMs;
}

/**
 * Inverse of `partsInZone` for a minute-aligned wall time: given a Y-M-D and
 * minutes-past-midnight IN `tz`, return the absolute UTC instant (ms). Two-pass
 * so a DST boundary resolves correctly (Asia/Kolkata has no DST, so the second
 * pass is a no-op there, but the engine stays correct for any zone).
 */
function instantFromZonedWall(
  y: number,
  mo: number,
  d: number,
  min: number,
  tz: string,
): number {
  const naiveUtc = Date.UTC(y, mo - 1, d, Math.floor(min / 60), min % 60, 0);
  const offset1 = zoneOffsetMsAt(naiveUtc, tz);
  let instant = naiveUtc - offset1;
  const offset2 = zoneOffsetMsAt(instant, tz);
  if (offset2 !== offset1) instant = naiveUtc - offset2;
  return instant;
}

const DAY_MS = 86_400_000;

/**
 * THE availability engine. Pure + deterministic: every time input is an ISO
 * string param, never a wall clock read inside. Returns the bookable open
 * slots, sorted ascending by start, that satisfy ALL the locked rules.
 *
 * @param busy     Busy intervals (Google free/busy, normalised). Empty = fully free.
 * @param weekly   The global per-weekday windows (settings/availability.weekly).
 * @param override Optional per-type custom weekly windows; when provided it
 *                 REPLACES `weekly` (the resolveWindows "custom" branch). Pass
 *                 null/undefined to use the global windows.
 * @param params   Booking rules + the `nowISO` clock + `timeZone` + blockedDates.
 */
export function computeOpenSlots(
  busy: FreeBusyInterval[],
  weekly: WeeklyAvailability,
  override: WeeklyAvailability | null | undefined,
  params: ComputeOpenSlotsParams,
): OpenSlot[] {
  const {
    durationMin,
    bufferMin,
    minNoticeHours,
    maxPerDay,
    horizonDays,
    nowISO,
    timeZone,
    blockedDates,
  } = params;

  const nowMs = Date.parse(nowISO);
  if (Number.isNaN(nowMs)) return [];
  if (durationMin <= 0) return [];

  const effectiveWeekly: WeeklyAvailability = override ?? weekly;
  const blocked = new Set(blockedDates ?? []);

  const earliestMs = nowMs + minNoticeHours * 60 * 60 * 1000;
  const horizonEndMs = nowMs + horizonDays * DAY_MS;

  // Pre-expand busy intervals by the buffer (parse once). Drop unparseable.
  const expandedBusy = busy
    .map((b) => ({ s: Date.parse(b.startISO), e: Date.parse(b.endISO) }))
    .filter((b) => !Number.isNaN(b.s) && !Number.isNaN(b.e) && b.e > b.s)
    .map((b) => ({ s: b.s - bufferMin * 60_000, e: b.e + bufferMin * 60_000 }));
  // Raw busy starts (un-buffered) used to count existing bookings per day.
  const rawBusy = busy
    .map((b) => ({ s: Date.parse(b.startISO), e: Date.parse(b.endISO) }))
    .filter((b) => !Number.isNaN(b.s) && !Number.isNaN(b.e) && b.e > b.s);

  const overlapsBusy = (s: number, e: number): boolean =>
    expandedBusy.some((b) => s < b.e && e > b.s);

  // Anchor the day walk at NOON in the stored zone so a +24h step never drifts
  // into an adjacent calendar day (robust even across DST in other zones).
  const nowParts = partsInZone(new Date(nowMs), timeZone);
  const noonAnchorMs = Date.UTC(
    nowParts.y,
    nowParts.mo - 1,
    nowParts.d,
    12,
    0,
    0,
  );

  const out: OpenSlot[] = [];

  for (let dayOffset = 0; dayOffset <= horizonDays; dayOffset++) {
    const dayParts = partsInZone(
      new Date(noonAnchorMs + dayOffset * DAY_MS),
      timeZone,
    );
    const dateStr = ymd(dayParts);

    // Hard block: passed-in blocked dates.
    if (blocked.has(dateStr)) continue;

    const windows = effectiveWeekly[dayParts.dow] ?? [];
    if (windows.length === 0) continue; // day off (e.g. Sundays = empty array)

    // Existing bookings on this calendar day count against the per-day cap.
    const bookingsOnDay = rawBusy.filter((b) => {
      const bp = partsInZone(new Date(b.s), timeZone);
      return ymd(bp) === dateStr;
    }).length;
    let remainingForDay = maxPerDay - bookingsOnDay;
    if (remainingForDay <= 0) continue;

    // Collect this day's candidate slots in window/time order.
    const dayCandidates: OpenSlot[] = [];
    for (const w of windows) {
      for (
        let startMin = w.startMin;
        startMin + durationMin <= w.endMin;
        startMin += durationMin
      ) {
        const startMs = instantFromZonedWall(
          dayParts.y,
          dayParts.mo,
          dayParts.d,
          startMin,
          timeZone,
        );
        const endMs = startMs + durationMin * 60_000;

        if (startMs < earliestMs) continue; // min-notice
        if (startMs > horizonEndMs) continue; // horizon
        if (overlapsBusy(startMs, endMs)) continue; // busy + buffer

        dayCandidates.push({
          startISO: new Date(startMs).toISOString(),
          endISO: new Date(endMs).toISOString(),
          audienceTag: w.audience,
        });
      }
    }

    // Earliest-first, then apply the per-day cap.
    dayCandidates.sort((a, b) => a.startISO.localeCompare(b.startISO));
    for (const slot of dayCandidates) {
      if (remainingForDay <= 0) break;
      out.push(slot);
      remainingForDay--;
    }
  }

  out.sort((a, b) => a.startISO.localeCompare(b.startISO));
  return out;
}
